import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { healthService, HealthProgramGroupDto, setCurrentUser } from '../../services/health';
import { useAuth } from 'react-oidc-context';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import PageHeader from '../../components/UI/PageHeader';
// import StatusBadge from '../../components/UI/StatusBadge'; // Não está sendo usado
// import ConfirmDialog from '../../components/UI/ConfirmDialog'; // Não está sendo usado
import Pagination from '../../components/UI/Pagination';
import { formatDateTime } from '../../utils/formatters';

const ProgramGroupsList: React.FC = () => {
  const navigate = useNavigate();
  const auth = useAuth();

  // Update the current user for the health service
  useEffect(() => {
    setCurrentUser(auth.user || null);
  }, [auth.user]);
  const [programGroups, setProgramGroups] = useState<HealthProgramGroupDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(10);  // setPageSize não está sendo usado

  const fetchProgramGroups = async () => {
    setLoading(true);
    try {
      const response = await healthService.getProgramGroups(currentPage, pageSize);
      setProgramGroups(response.content || []);
      setTotalElements(response.total_elements || 0);
      setCurrentPage(currentPage);
    } catch (error) {
      console.error('Error fetching program groups:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProgramGroups();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleViewDetails = (id: string) => {
    navigate(`/health/program-groups/${id}`);
  };

  const handleCreateNew = () => {
    navigate('/health/program-groups/create');
  };

  return (
    <div>
      <PageHeader
        title="Health Program Groups"
        breadcrumbs={[
          { label: "Dashboard", to: "/" },
          { label: "Health" },
          { label: "Program Groups" }
        ]}
      />

      <div className="mb-6">
        <Button variant="primary" onClick={handleCreateNew}>
          Create New Group
        </Button>
      </div>

      <Card>
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Created At
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Updated At
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-900 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {programGroups.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500">
                        No program groups found
                      </td>
                    </tr>
                  ) : (
                    programGroups.map((group) => (
                      <tr key={group.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {group.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {group.description}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {group.created_at ? formatDateTime(group.created_at) : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {group.updated_at ? formatDateTime(group.updated_at) : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => group.id && handleViewDetails(group.id)}
                          >
                            View Details
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            
            {totalElements > pageSize && (
              <div className="py-3 px-6 border-t border-gray-200">
                <Pagination
                  currentPage={currentPage}
                  totalItems={totalElements}
                  pageSize={pageSize}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default ProgramGroupsList;